import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { 
  Zap, 
  TrendingDown, 
  TrendingUp, 
  Award, 
  Briefcase,
  MapPin,
  BarChart3,
  Monitor,
  Bell,
  Users,
  ArrowRight,
  ChevronRight,
  Phone,
  Cpu,
  Shield,
  Globe
} from 'lucide-react'

const stats = [
  { icon: TrendingDown, value: '30', suffix: '%', label: '运营成本降低' },
  { icon: TrendingUp, value: '50', suffix: '%+', label: '运营效率提升' },
  { icon: Award, value: '5', suffix: '项', label: '专业资质' },
  { icon: Briefcase, value: '10', suffix: '+', label: '成功案例' },
]

const services = [
  {
    icon: MapPin,
    title: '投建规划',
    description: '大数据支撑科学选址，定制化设备选型推荐，精准投资回报模型评估',
    features: ['选址分析', '设备选型', '投资回报模型'],
    color: 'from-neon-cyan to-neon-blue',
  },
  {
    icon: Monitor,
    title: '智能运维',
    description: '实时远程监控，智能故障预警，能效优化降低运营成本',
    features: ['远程监控', '故障预警', '能效优化'],
    color: 'from-neon-green to-emerald-400',
  },
  {
    icon: Users,
    title: '品牌运营',
    description: '多平台流量接入，用户运营工具，车后增值服务拓展',
    features: ['流量接入', '用户运营', '增值服务'],
    color: 'from-neon-purple to-violet-400',
  },
]

const values = [
  {
    icon: Zap,
    title: '智能速应',
    description: 'AI智能客服系统，7×24小时不间断响应',
  },
  {
    icon: BarChart3,
    title: '全域服务',
    description: '从合作洽谈至长期运维，搭建"需求-交付-复盘"闭环',
  },
  {
    icon: Users,
    title: '专属直达',
    description: '1V1专属服务小组，含行业顾问、技术专家',
  },
  {
    icon: Bell,
    title: '应急稳续',
    description: '24小时待命应急小组，快速处理故障类问题',
  },
]

const partners = [
  { name: '高德地图', logo: 'https://down.dingtalk.com/media/lQLPM45d6YNBNGnNBOLND6CwYIeeDi5gypIJsct5FCfgAA_4000_1250.png' },
  { name: '阿里云', logo: 'https://down.dingtalk.com/media/lQLPM4sjLosx8JnNAmfNB9CwJw15IfwOtwgJscNdgsZjAw_2000_615.png' },
  { name: '腾讯', logo: 'https://down.dingtalk.com/media/lQLPD3HQp3bDwZnNAVDNBsmwMwlW0A1SVG8JscNdgsZjBA_1737_336.png' },
  { name: '地上铁', logo: 'https://down.dingtalk.com/media/lQLPD4Tko_XnE6nNAnbNB3SwRz_w_OHJ6AwJscjjBVTGAA_1908_630.png' },
  { name: '云快充', logo: 'https://down.dingtalk.com/media/lQLPM5IrEk8DGinNArXNBwqwUMQzFhdx9sQJscv4jcqDAA_1802_693.png' },
]

function AnimatedCounter({ value, suffix }: { value: string; suffix: string }) {
  const [count, setCount] = useState(0)
  const targetValue = parseInt(value)
  const ref = useRef<HTMLSpanElement>(null)
  const hasAnimated = useRef(false)

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated.current) {
            hasAnimated.current = true
            const duration = 2000
            const startTime = performance.now()

            const animate = (currentTime: number) => {
              const elapsed = currentTime - startTime
              const progress = Math.min(elapsed / duration, 1)
              const easeOutQuart = 1 - Math.pow(1 - progress, 4)
              const current = Math.floor(easeOutQuart * targetValue)
              setCount(current)

              if (progress < 1) {
                requestAnimationFrame(animate)
              }
            }

            requestAnimationFrame(animate)
          }
        })
      },
      { threshold: 0.5 }
    )

    if (ref.current) {
      observer.observe(ref.current)
    }

    return () => observer.disconnect()
  }, [targetValue])

  return (
    <span ref={ref} className="text-gradient">
      {count}{suffix}
    </span>
  )
}

export default function Home() {
  return (
    <div className="overflow-hidden bg-dark">
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0">
          <img
            src="/Users/chengyu/.real/users/user-5f527bf594d9557efa489ce428595395/workspace/yidianli-website/ev_charging.png"
            alt="EV Charging Station"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-dark via-dark/90 to-dark" />
          <div className="absolute inset-0 bg-gradient-to-r from-dark via-transparent to-dark/50" />
        </div>

        {/* Grid Pattern */}
        <div className="absolute inset-0 grid-pattern opacity-50" />

        {/* Glowing Orbs */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-neon-green/10 rounded-full blur-[100px] animate-pulse-slow" />
        <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-neon-cyan/10 rounded-full blur-[80px] animate-pulse-slow" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 right-1/3 w-48 h-48 bg-neon-purple/10 rounded-full blur-[60px] animate-pulse-slow" style={{ animationDelay: '2s' }} />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32">
          <div className="text-center max-w-4xl mx-auto">
            {/* Badge */}
            <div className="inline-flex items-center space-x-2 px-4 py-2 rounded-full glass-card mb-8 animate-fade-in">
              <span className="w-2 h-2 rounded-full bg-neon-green animate-pulse" />
              <span className="text-neon-green text-sm font-medium">新能源汽车充电产业生态服务商</span>
            </div>
            
            {/* Main Title */}
            <h1 className="text-4xl font-bold text-white leading-tight mb-6 animate-slide-up">
              <span className="block">技术驱动</span>
              <span className="block text-gradient mt-2">生态聚合 · 全域赋能</span>
            </h1>
            
            <p className="text-base text-white/60 mb-10 max-w-2xl mx-auto animate-slide-up" style={{ animationDelay: '0.1s' }}>
              壹电利（ONEEC）专注新能源领域创新服务，以自主研发的"壹电利充"聚合充电运营管理平台为核心，助力运营商实现充电场景优化、效率提升及全流程数字化管理。
            </p>
            
            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-slide-up" style={{ animationDelay: '0.2s' }}>
              <Link
                to="/services"
                className="inline-flex items-center justify-center px-8 py-4 bg-neon-green text-dark rounded-xl font-semibold hover:bg-neon-green/90 transition-all hover:scale-105 neon-glow-strong"
              >
                了解我们的服务
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-8 py-4 glass-card text-white rounded-xl font-semibold hover:bg-white/10 transition-all group"
              >
                联系我们
                <ChevronRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </div>

        {/* Bottom Gradient */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-dark to-transparent" />
      </section>

      {/* Stats Section */}
      <section className="py-20 md:py-28 relative">
        <div className="absolute inset-0 radial-glow opacity-50" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {stats.map((stat, index) => (
              <div
                key={stat.label}
                className="glass-card p-6 md:p-8 hover:border-neon-green/30 transition-all duration-300 group"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <stat.icon className="w-8 h-8 text-neon-green mb-4 group-hover:scale-110 transition-transform" />
                <div className="text-3xl md:text-4xl font-bold mb-2">
                  <AnimatedCounter value={stat.value} suffix={stat.suffix} />
                </div>
                <p className="text-white/50 text-sm">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 md:py-28 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-flex items-center space-x-2 px-4 py-2 rounded-full glass-card mb-4">
              <Cpu className="w-4 h-4 text-neon-green" />
              <span className="text-neon-green text-sm font-medium">核心服务</span>
            </span>
            <h2 className="text-3xl font-bold text-white mb-4">
              三大服务板块<span className="text-gradient">，全方位赋能</span>
            </h2>
            <p className="text-white/50 max-w-2xl mx-auto">
              从投建规划到智能运维，再到品牌运营，我们为充电运营商提供全生命周期的专业服务
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <div
                key={service.title}
                className="glass-card p-8 hover:border-neon-green/30 transition-all duration-300 hover:-translate-y-1 group"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${service.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform neon-glow`}>
                  <service.icon className="w-7 h-7 text-dark" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{service.title}</h3>
                <p className="text-white/50 mb-6 leading-relaxed">{service.description}</p>
                <div className="flex flex-wrap gap-2">
                  {service.features.map((feature) => (
                    <span
                      key={feature}
                      className="px-3 py-1 bg-white/5 text-white/70 text-sm rounded-full border border-white/10"
                    >
                      {feature}
                    </span>
                  ))}
                </div>
                <Link
                  to="/services"
                  className="inline-flex items-center mt-6 text-neon-green font-medium hover:gap-2 transition-all group/link"
                >
                  了解更多
                  <ChevronRight className="w-4 h-4 ml-1 group-hover/link:translate-x-1 transition-transform" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 md:py-28 relative">
        <div className="absolute inset-0 radial-glow opacity-30" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-16">
            <span className="inline-flex items-center space-x-2 px-4 py-2 rounded-full glass-card mb-4">
              <Shield className="w-4 h-4 text-neon-green" />
              <span className="text-neon-green text-sm font-medium">服务价值</span>
            </span>
            <h2 className="text-3xl font-bold text-white mb-4">
              为什么选择<span className="text-gradient">壹电利</span>
            </h2>
            <p className="text-white/50 max-w-2xl mx-auto">
              我们以客户为中心，提供专业、高效、贴心的服务体验
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {values.map((value, index) => (
              <div
                key={value.title}
                className="glass-card p-6 hover:border-neon-green/30 transition-all duration-300 group"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="w-12 h-12 rounded-xl bg-neon-green/10 flex items-center justify-center mb-4 group-hover:bg-neon-green group-hover:scale-110 transition-all">
                  <value.icon className="w-6 h-6 text-neon-green group-hover:text-dark transition-colors" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{value.title}</h3>
                <p className="text-white/50 text-sm leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partners Section */}
      <section className="py-20 md:py-28 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-flex items-center space-x-2 px-4 py-2 rounded-full glass-card mb-4">
              <Globe className="w-4 h-4 text-neon-green" />
              <span className="text-neon-green text-sm font-medium">合作伙伴</span>
            </span>
            <h2 className="text-3xl font-bold text-white mb-4">
              携手<span className="text-gradient">行业领军企业</span>
            </h2>
            <p className="text-white/50 max-w-2xl mx-auto">
              与行业头部企业深度合作，整合优势资源，共建充电产业生态，为客户提供更优质的服务
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {partners.map((partner) => (
              <div
                key={partner.name}
                className="bg-white p-4 hover:border-neon-green/30 transition-all group flex items-center justify-center h-20 rounded-xl"
              >
                <img
                  src={partner.logo}
                  alt={partner.name}
                  className="max-h-10 max-w-full object-contain"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    target.parentElement!.innerHTML = `<span class="text-white font-semibold text-sm">${partner.name}</span>`;
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-28 relative">
        <div className="absolute inset-0 radial-glow opacity-50" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <h2 className="text-3xl font-bold text-white mb-6">
            开启您的充电运营<span className="text-gradient">数字化之旅</span>
          </h2>
          <p className="text-white/50 text-lg mb-10 max-w-2xl mx-auto">
            立即联系我们，获取专业的投建规划咨询，让壹电利助力您的充电业务腾飞
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-8 py-4 bg-neon-green text-dark rounded-xl font-semibold hover:bg-neon-green/90 transition-all hover:scale-105 neon-glow-strong"
            >
              立即咨询
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
            <a
              href="tel:17778002535"
              className="inline-flex items-center justify-center px-8 py-4 glass-card text-white rounded-xl font-semibold hover:bg-white/10 transition-all group"
            >
              <Phone className="mr-2 w-5 h-5 text-neon-green" />
              177-7800-2535
            </a>
          </div>
        </div>
      </section>
    </div>
  )
}
